import { CreatedDateDB } from "@/app/(protected)/saved-projects/types";

export type TimeDifference = Date | number | undefined;

/**
 * Calculate the time difference between the current time and a given previous time.
 * @param {Date | number} previousTime - The previous time as a Date object or timestamp (milliseconds).
 * @returns {string} A string representing the time difference in the format "Ww Xd Yh Zm As".
 */
export const getTimeDifference = (previousTime: TimeDifference): string => {
  if (!previousTime) return "";
  const currentTime = Date.now();
  const previousTimestamp = previousTime instanceof Date ? previousTime.getTime() : previousTime;

  let diffInSeconds = Math.max(0, Math.floor((currentTime - previousTimestamp) / 1000));

  const units = [
    { label: "w", seconds: 7 * 24 * 60 * 60 }, // Weeks
    { label: "d", seconds: 24 * 60 * 60 }, // Days
    { label: "h", seconds: 60 * 60 }, // Hours
    { label: "m", seconds: 60 }, // Minutes
    { label: "s", seconds: 1 }, // Seconds
  ];

  const result = units.reduce((acc, { label, seconds }) => {
    if (diffInSeconds >= seconds) {
      const value = Math.floor(diffInSeconds / seconds);
      diffInSeconds %= seconds;
      acc.push(`${value}${label}`);
    }
    return acc;
  }, [] as string[]);

  return result.length ? result.join(" ") : "0s";
};

export const getFormattedDate = (date: CreatedDateDB | string, formatFn?: (dateObj: Date) => string) => {
  if (!date) return "";

  let dateObj: Date;
  if (typeof date === "object") {
    const timestamp = date as { _seconds: number; _nanoseconds: number };
    dateObj = new Date(timestamp._seconds * 1000);
  } else if (typeof date === "string") {
    dateObj = new Date(date);
  } else {
    return "";
  }
  return formatFn ? formatFn(dateObj) : dateObj.toLocaleDateString("en-US");
};
